(() => {
  const init = root => {
    if (root.dataset.vfReady) return;
    root.dataset.vfReady = 'true';
    const controller = new AbortController();
    const { signal } = controller;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const clamp = value => Math.max(0, Math.min(1, value));
    const mix = (from, to, progress) => from + (to - from) * progress;
    let motion = root.dataset.motion !== 'false' && !reduceMotion.matches;
    let raf = 0;
    let disposed = false;
    root.classList.toggle('has-motion', motion);
    const artifact = root.querySelector('.vf-artifact');
    const introFilm = root.querySelector('.vf-story-intro .vf-story-film');
    const scene = root.querySelector('.vf-artifact-scene');
    const layers = [...root.querySelectorAll('.vf-artifact-layer')];
    const clouds = [...root.querySelectorAll('.vf-artifact-cloud')];
    const special = root.querySelector('.vf-special');
    const flights = [...root.querySelectorAll('.vf-special-flight')];
    const rotation = root.querySelector('[data-vf-rotation]');
    const canvas = rotation?.querySelector('canvas');
    const context = canvas?.getContext('2d');
    const chapterArticles = [...root.querySelectorAll('[data-vf-chapter]')];
    const chapterImages = [...root.querySelectorAll('[data-vf-chapter-image]')];
    const craftItems = [...root.querySelectorAll('.vf-craft-item')];
    const animated = [];
    let desiredFrame = 1;
    let paintedFrame = 0;
    let framesStarted = false;
    let activeFrameLoads = 0;
    let frameQueue = [];
    const frames = new Map();
    const framePattern = rotation ? (window.innerWidth <= 767 ? rotation.dataset.frameMobile : rotation.dataset.frameDesktop) : '';
    let rotationLift = 0;
    const apply = (element, transform) => {
      if (!element) return;
      element.style.transform = transform;
      if (!animated.includes(element)) animated.push(element);
    };
    const draw = () => {
      if (!context || !frames.size || disposed || !motion) return;
      const available = [...frames.keys()].sort((a, b) => Math.abs(a - desiredFrame) - Math.abs(b - desiredFrame));
      const frame = available[0];
      if (paintedFrame === frame) return;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(frames.get(frame), 0, 0, canvas.width, canvas.height);
      paintedFrame = frame;
      canvas.dataset.renderedFrame = String(frame);
      rotation.classList.add('has-frame');
    };
    const pumpFrames = () => {
      if (disposed || !motion) return;
      while (activeFrameLoads < 4 && frameQueue.length) {
        const index = frameQueue.shift();
        activeFrameLoads++;
        const image = new Image();
        image.decoding = 'async';
        const done = () => {
          activeFrameLoads--;
          draw();
          pumpFrames();
        };
        image.onload = () => {
          if (!disposed) frames.set(index, image);
          done();
        };
        image.onerror = done;
        image.src = framePattern.replace('FRAME', String(index).padStart(2, '0'));
      }
    };
    const startFrames = () => {
      if (!context || !framePattern || framesStarted || !motion) return;
      framesStarted = true;
      const priority = [1, 30, 58, 15, 45, 8, 22, 38, 52];
      frameQueue = [...priority, ...Array.from({ length: 58 }, (_, index) => index + 1).filter(index => !priority.includes(index))];
      pumpFrames();
    };
    const sizeCanvas = () => {
      if (!canvas) return;
      const resolution = Math.round(Math.min(2, window.devicePixelRatio || 1) * Math.min(800, Math.max(500, window.innerWidth)));
      if (canvas.width !== resolution) {
        canvas.width = resolution;
        canvas.height = resolution;
        paintedFrame = 0;
      }
    };
    const update = () => {
      raf = 0;
      if (disposed) return;
      const height = window.innerHeight;
      const mobile = window.innerWidth <= 767;
      const tablet = window.innerWidth <= 1024;
      if (motion && artifact) {
        const rect = artifact.getBoundingClientRect();
        if (rect.bottom > -500 && rect.top < height + 500) {
          const progress = clamp((height - rect.top) / 1000);
          const drift = clamp((height - rect.top - (mobile ? 320 : 600)) / 2000);
          apply(scene, `translateY(${drift * (mobile ? 10 : 25)}%) scale(${mobile ? 1 : mix(.7, 1, progress)})`);
          layers.forEach((layer, index) => {
            if (index === 0) apply(layer, `translateY(${mix(50, 0, progress)}%)`);
            if (index === 1) apply(layer, `translateY(${mix(mobile ? 120 : 150, 0, progress)}%)`);
            if (index === 2) apply(layer, `translate(${mix(50, 0, progress)}%,${mobile ? mix(50, 0, progress) : 0}%)`);
            if (index === 6) apply(layer, `scale(${mix(.8, 1, progress)})`);
            if (index === 7) apply(layer, `translateX(${mix(-50, 0, progress)}%)`);
          });
          const cloudProgress = clamp((height - rect.top) / 2000);
          clouds.forEach((cloud, index) => apply(cloud, `translateX(${(index === 0 || index === 3 ? -70 : 70) * cloudProgress}%)`));
          apply(introFilm, `scale(${mix(1.4, 1, 1 - Math.pow(1 - cloudProgress, 2))})`);
        }
      }
      if (motion && special) {
        const rect = special.getBoundingClientRect();
        if (rect.bottom > -500 && rect.top < height + 500) {
          const progress = clamp((height - rect.top) / 4000);
          apply(flights[0], `translate(${progress * (mobile ? -70 : 70)}%,${mobile ? progress * -100 : 0}%)`);
          apply(flights[1], `translateX(${progress * 30}%)`);
          apply(flights[2], `translate(${!mobile && window.innerWidth <= 1280 ? progress * 100 : 0}%,${progress * (mobile ? -200 : -300)}%)`);
          apply(flights[3], `translate(${progress * 90}%,${progress * -300}%)`);
          if (!tablet) {
            const intro = clamp((height - rect.top) / 1000);
            apply(special.querySelector('.vf-story-star'), `translateY(${mix(50, -30, intro)}%)`);
            apply(special.querySelector('.vf-story-title'), `translateY(${mix(30, -20, intro)}%)`);
            apply(special.querySelector('.vf-story-text'), `translateY(${mix(150, -60, intro)}%)`);
            apply(special.querySelector('.vf-special-castle'), `translateY(${mix(50, -15, intro)}%)`);
          }
        }
      }
      if (motion && !tablet && special && rotation) {
        const rect = special.getBoundingClientRect();
        const castle = special.querySelector('.vf-special-castle');
        rotationLift = clamp((height - rect.top - (castle?.offsetTop || 0)) / 1500) * -.3;
        apply(rotation, `translateY(${rotationLift * 100}%)`);
      }
      if (rotation) {
        const rect = rotation.getBoundingClientRect();
        const top = rect.top - rect.height * rotationLift;
        if (rect.top < height + 1000 && rect.bottom > -1000) startFrames();
        desiredFrame = Math.max(1, Math.min(58, Math.floor(clamp((height - top - 300) / 1100) * 58) + 1));
        if (motion) {
          draw();
          if (!tablet) apply(rotation.querySelector('.vf-rotation-copy'), `translateY(${mix(50, -100, clamp((height - top - 600) / 2000))}%)`);
        }
      }
      if (motion && !tablet && chapterArticles.length) {
        let current = 0;
        chapterArticles.forEach((article, index) => {
          if (article.getBoundingClientRect().top <= height * .5) current = index;
        });
        chapterImages.forEach((image, index) => image.classList.toggle('is-active', index === current));
      }
      if (motion && window.innerWidth > 1280) {
        craftItems.forEach(item => {
          const rect = item.getBoundingClientRect();
          if (rect.top > height + 400 || rect.bottom < -400) return;
          const progress = clamp((height - rect.top) / 2000);
          apply(item.querySelector('.vf-craft-image'), `translateY(${mix(30, -15, progress)}%)`);
          apply(item.querySelector('.vf-craft-copy'), `translateY(${mix(450, -50, progress)}%)`);
        });
      }
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    const refresh = () => {
      animated.forEach(element => { element.style.transform = ''; });
      rotationLift = 0;
      sizeCanvas();
      schedule();
    };
    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', refresh, { passive: true, signal });
    reduceMotion.addEventListener('change', () => {
      motion = root.dataset.motion !== 'false' && !reduceMotion.matches;
      paintedFrame = 0;
      root.classList.toggle('has-motion', motion);
      if (!motion) {
        root.querySelectorAll('video').forEach(video => { if (video.muted) video.pause(); });
        rotation?.classList.remove('has-frame');
      } else {
        pumpFrames();
      }
      refresh();
    }, { signal });
    root.querySelectorAll('[data-vf-comparison]').forEach(comparison => {
      const range = comparison.querySelector('input[type="range"]');
      range.addEventListener('input', () => comparison.style.setProperty('--vf-compare', `${range.value}%`), { signal });
      range.addEventListener('pointerdown', event => {
        const rect = range.getBoundingClientRect();
        range.value = String(Math.round(clamp((event.clientX - rect.left) / rect.width) * 100));
        comparison.style.setProperty('--vf-compare', `${range.value}%`);
      }, { signal });
    });
    const filmObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const film = entry.target;
        const video = film.querySelector('video');
        if (entry.isIntersecting && film.dataset.preview && !film.dataset.userPlayed && !film.classList.contains('is-playing')) {
          if (!video.src) {
            video.src = window.innerWidth <= 767 && film.dataset.previewMobile ? film.dataset.previewMobile : film.dataset.preview;
            video.load();
          }
          if (motion) video.play().catch(() => {});
        } else if (!entry.isIntersecting) {
          video.pause();
        }
      });
    }, { rootMargin: '100px 0px', threshold: .05 }) : null;
    root.querySelectorAll('[data-vf-film]').forEach(film => {
      const video = film.querySelector('video');
      const button = film.querySelector('button');
      if (window.innerWidth <= 767 && film.dataset.posterMobile) video.poster = film.dataset.posterMobile;
      filmObserver?.observe(film);
      button?.addEventListener('click', () => {
        root.querySelectorAll('video').forEach(other => { if (other !== video) other.pause(); });
        const fullSource = window.innerWidth <= 767 && film.dataset.fullMobile ? film.dataset.fullMobile : film.dataset.full;
        if (!fullSource) return;
        film.dataset.userPlayed = 'true';
        if (video.src !== fullSource) {
          video.src = fullSource;
          video.load();
        }
        video.muted = false;
        video.loop = false;
        video.controls = true;
        film.classList.add('is-playing');
        video.play().catch(() => { film.classList.remove('is-playing'); });
      }, { signal });
      video.addEventListener('ended', () => { film.classList.remove('is-playing'); }, { signal });
      video.addEventListener('error', () => { film.classList.remove('is-playing'); }, { signal });
    });
    const track = root.querySelector('[data-vf-reader-track]');
    const previous = root.querySelector('[data-vf-reader-prev]');
    const next = root.querySelector('[data-vf-reader-next]');
    if (track && previous && next) {
      const updateButtons = () => {
        const loop = window.innerWidth < 992;
        const canScroll = track.scrollWidth - track.clientWidth > 3;
        previous.disabled = !canScroll || (!loop && track.scrollLeft <= 3);
        next.disabled = !canScroll || (!loop && track.scrollLeft + track.clientWidth >= track.scrollWidth - 3);
      };
      const shift = direction => {
        const card = track.querySelector('.vf-reader-card');
        const end = Math.max(0, track.scrollWidth - track.clientWidth);
        if (window.innerWidth < 992 && end > 3) {
          const wrapToStart = direction > 0 && track.scrollLeft >= end - 3;
          const wrapToEnd = direction < 0 && track.scrollLeft <= 3;
          if (wrapToStart || wrapToEnd) {
            track.scrollTo({ left: wrapToStart ? 0 : end, behavior: 'instant' });
            return;
          }
        }
        track.scrollBy({ left: direction * ((card?.getBoundingClientRect().width || 320) + 10), behavior: motion ? 'smooth' : 'auto' });
      };
      previous.addEventListener('click', () => shift(-1), { signal });
      next.addEventListener('click', () => shift(1), { signal });
      track.addEventListener('scroll', updateButtons, { passive: true, signal });
      window.addEventListener('resize', updateButtons, { passive: true, signal });
      updateButtons();
    }
    const destroy = event => {
      if (event.target.id !== `shopify-section-${root.dataset.sectionId}`) return;
      disposed = true;
      controller.abort();
      filmObserver?.disconnect();
      cancelAnimationFrame(raf);
      root.querySelectorAll('video').forEach(video => video.pause());
      frames.clear();
    };
    document.addEventListener('shopify:section:unload', destroy, { signal });
    document.addEventListener('shopify:block:select', event => {
      const chapter = event.target.closest('[data-vf-chapter]');
      if (chapter && root.contains(chapter)) {
        const selected = Number(chapter.dataset.vfChapter);
        chapterImages.forEach((image, index) => image.classList.toggle('is-active', index === selected));
      }
    }, { signal });
    sizeCanvas();
    schedule();
  };
  document.querySelectorAll('[data-vf-story]').forEach(init);
  if (!window.vfStorySectionListener) {
    window.vfStorySectionListener = true;
    document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-vf-story]').forEach(init));
  }
})();
